import PdaTitle from '../../component/common/PdaTitle'
import { InputText } from 'primereact/inputtext'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { CommonContext } from '../../component/common/context/commonContext'
import { getContentSize } from '../../utils/screenUtils'
import { useMountEffect } from 'primereact/hooks'
import { debounce, isEmpty, size } from 'lodash'
import { ScrollPanel } from 'primereact/scrollpanel'
import { classNames } from 'primereact/utils'
import { Button } from 'primereact/button'
import { MidmileIncomingSearch } from '../../types/inbound'
import CommonProgressSpinner from '../../component/common/CommonProgressSpinner'
import { useTranslation } from 'react-i18next'
import { tokens } from 'locales/tokens'

const MiddleMile = () => {
    const { $http, $alert, $toast } = useContext(CommonContext)
    const { t } = useTranslation()
    const scanRef = useRef<HTMLInputElement>(null)
    const [mdlmlPltNo, setMdlMlPltNo] = useState<string>('')
    const [buttonLabel, setButtonLabel] = useState<string>('')
    const [height, setHieght] = useState<number>(getContentSize())
    const [disabledButton, setDisabledButton] = useState<boolean>(false)
    const [pallets, setPallets] = useState<MiddleMileData[]>()
    const [loading, setLoading] = useState<boolean>(false)
    const [params, setParams] = useState<MiddleMileSearch>({
        mdlmlPltNo: '',
    })

    useMountEffect(() => {
        window.navigator.clipboard.writeText('')
        setTimeout(() => {
            if (scanRef && scanRef.current) {
                scanRef.current.focus()
            }
        }, 300)
    })

    const findMiddleMile = useCallback(
        async (params: MidmileIncomingSearch) => {
            console.log('params.mdlmlPltNo', params.mdlmlPltNo)
            if (params.mdlmlPltNo) {
                const result = await $http().get('/outbound/middle-mile', {
                    params: { ...params },
                })

                if (isEmpty(result)) {
                    setDisabledButton(false)
                    setButtonLabel(`${t(tokens.outbound.midmile_outgoing_complete_processing)} ${findCount(result)}`)
                    setPallets(result)
                    setMdlMlPltNo('')
                    scanRef.current?.focus()
                    scanRef.current?.select()
                    setTimeout(() => setHieght(getContentSize()), 300)
                    return
                }

                const isComplete = result[0].mdlmlOutbSttCd !== 'DW'
                setDisabledButton(isComplete)
                setButtonLabel(
                    isComplete
                        ? `${t(tokens.outbound.midmile_outgoing_complete)} ${findCount(result)}`
                        : `${t(tokens.outbound.midmile_outgoing_complete_processing)} ${findCount(result)}`,
                )
                setPallets(result)
                setMdlMlPltNo('')
                setTimeout(() => setHieght(getContentSize()), 300)
            }

            scanRef.current?.focus()
            scanRef.current?.select()
        },
        [$http, $toast],
    )

    useEffect(() => {
        if (params.mdlmlPltNo) {
            findMiddleMile(params)
        }
    }, [params, findMiddleMile])

    useEffect(() => {
        window.navigator.clipboard.readText().then((result) => {
            if (result) {
                setParams({
                    ...params,
                    mdlmlPltNo: result,
                })
            }
        })
    }, [window.navigator.clipboard])

    const onKeyDownSearch = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
            setParams({
                ...params,
                mdlmlPltNo,
            })
        }
    }

    const getCardStyle = (scanYn: string) => {
        if (scanYn) {
            return {
                background: '#90EE90',
                border: '1px solid #D3D3D3',
            }
        } else {
            return {
                background: '#F59E0B',
                border: '1px solid #D3D3D3',
            }
        }
    }

    const findCount = (result: any) => {
        if (isEmpty(result)) {
            return ''
        }

        const total = size(result)
        const scanCount = size(result?.filter((incoming: any) => incoming.outbScanYn === 'Y'))

        return `(${scanCount} / ${total})`
    }

    const onClickMidmileComplete = debounce(async () => {
        if (!pallets || isEmpty(pallets)) {
            return
        }

        const isNotMatched = size(pallets) !== size(pallets?.filter((pallet) => pallet.outbScanYn === 'Y'))

        if (isNotMatched) {
            const isConfirm = await $alert({
                icon: 'info',
                title: t(tokens.outbound.outgoing_confirmation),
                text: t(tokens.outbound.less_then_expted_midlmile_plt_quantity),
                isConfirm: true,
            })

            if (!isConfirm) {
                return
            }
        }
        setLoading(true)
        // DC001DC004-230824-01-01
        const mdlmlId = pallets[0].mdlmlId

        const result = await $http().put('/outbound/middle-mile', {
            mdlmlId,
            isNotMatched,
        })

        setLoading(false)

        if (result) {
            $toast({ title: t(tokens.common.success), text: t(tokens.outbound.completed_midmile_outgoing), icon: 'success' })
            setPallets([])
        }
    }, 1000)

    const findStatusColor = (outbScanYn: string) => {
        return { color: outbScanYn === 'Y' ? '#2E8B57' : 'red' }
    }

    return (
        <>
            {loading && <CommonProgressSpinner withBackground={true} />}
            <PdaTitle title={t(tokens.outbound.midmile_outgoing)} historyBack goToHome />
            <div className="grid pda-search p-3">
                <div className="col-12 flex">
                    <InputText
                        ref={scanRef}
                        value={mdlmlPltNo}
                        className="p-inputtext-md w-full"
                        placeholder={t(tokens.outbound.please_sacn_midmile_outbound_label_barcode)}
                        onChange={(e) => setMdlMlPltNo(e.currentTarget.value)}
                        onKeyDown={(e) => onKeyDownSearch(e)}
                        inputMode="none"
                        autoFocus={true}
                    />
                </div>
            </div>
            <div className="grid pl-2">
                {pallets && !isEmpty(pallets) ? (
                    <ScrollPanel className="flex w-full p-1" style={{ height: height }}>
                        {pallets.map((pallet) => (
                            <div className="mt-1" key={pallet.mdlmlPltNo}>
                                <div className="card w-full p-2" style={getCardStyle(pallet.outbScanYn)}>
                                    <div className="col-12 flex py-0 font-bold text-lg">
                                        <div className={classNames('col-3 text-left rdms-text-deepblue')}>
                                            {t(tokens.outbound.departure_and_arrival)}
                                        </div>
                                        <div className="col-5 align-self-center text-left">
                                            {pallet.dprCenterNm} / {pallet.arvCenterNm}
                                        </div>
                                        <div className="col-4 text-right">
                                            {pallet.pltNo} / {pallet.lblQty}
                                        </div>
                                    </div>
                                    <div className="col-12 justify-content-between flex py-0 align-items-center text-lg">
                                        <div className={classNames('col-3 text-left rdms-text-deepblue font-bold')}>
                                            {t(tokens.outbound.outbounded_at)}
                                        </div>
                                        <div className="col-5 text-left text-overflow-ellipsis overflow-hidden white-space-nowrap text-lg">
                                            {pallet.scanDt ? pallet.scanYmdhmFm : '-'}
                                        </div>
                                        <div className="col-4 text-right font-bold" style={findStatusColor(pallet.outbScanYn)}>
                                            {pallet.outbScanYn === 'Y'
                                                ? t(tokens.common.loading_inspection_completed)
                                                : t(tokens.common.loading_inspection_pending)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </ScrollPanel>
                ) : (
                    <div className="font-bold text-center w-full vertical-align-middle" style={{ paddingTop: height / 3 }}>
                        {t(tokens.outbound.please_sacn_midmile_outbound_label_barcode)}
                    </div>
                )}
            </div>
            <div
                className={classNames('pda-bottom', { 'display-none': isEmpty(pallets) })}
                style={{ position: 'absolute', bottom: 0, width: '100%', zIndex: 100 }}
            >
                <Button
                    label={buttonLabel}
                    size="large"
                    className="w-full p-button-info border-none"
                    onClick={onClickMidmileComplete}
                    disabled={disabledButton}
                />
            </div>
        </>
    )
}

export default MiddleMile
